import type { TFunction } from "i18next";

import { ApiClientError } from "./ApiClientError";

/**
 * Walks Error.cause for up to a few hops to find the underlying ApiClientError.
 * Returns null when none is present. Bounded depth keeps the function safe
 * against circular cause chains.
 */
function findApiClientError(error: unknown): ApiClientError | null {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current; depth++) {
    if (current instanceof ApiClientError) return current;
    if (current instanceof Error && current.cause !== undefined) {
      current = current.cause;
      continue;
    }
    return null;
  }
  return null;
}

/**
 * Returns a localized, user-friendly message when the error represents a
 * transport-level failure (network down, timeout) or a server-side failure
 * (HTTP >= 500). Returns null otherwise so the caller can fall back to its
 * domain-specific messaging (e.g. "invalid credentials" on login).
 *
 * Accepts any value and traverses Error.cause so callers that re-throw a
 * wrapper Error preserve the underlying ApiClientError.
 */
export function getNetworkOrServerErrorMessage(error: unknown, t: TFunction): string | null {
  const apiError = findApiClientError(error);
  if (!apiError) return null;

  if (apiError.code === "NETWORK_ERROR") {
    return t(
      "error.api.network",
      "No se puede conectar con el servidor. Verifica tu conexión a internet o inténtalo más tarde.",
    );
  }
  if (apiError.code === "TIMEOUT") {
    return t("error.api.timeout", "El servidor tardó demasiado en responder. Inténtalo de nuevo.");
  }
  if (apiError.status >= 500) {
    return t(
      "error.api.server",
      "Error del servidor. Nuestro equipo ha sido notificado. Inténtalo más tarde.",
    );
  }
  return null;
}

/**
 * Login-specific resolver. Returns a localized message for the cases where the
 * generic "invalid credentials" toast would mislead the user:
 *  - transport-level failures (network down, timeout, 5xx) — delegated to
 *    {@link getNetworkOrServerErrorMessage} so a 503 never reads as "suspended".
 *  - backend code `auth_account_suspended` — account deactivated by an admin.
 *
 * Returns null otherwise so the caller falls back to its generic message.
 */
export function getLoginErrorMessage(error: unknown, t: TFunction): string | null {
  const transportMessage = getNetworkOrServerErrorMessage(error, t);
  if (transportMessage) return transportMessage;

  const apiError = findApiClientError(error);
  if (apiError?.code === "auth_account_suspended") {
    return t(
      "auth.account_suspended",
      "Tu cuenta ha sido desactivada. Contacta con el administrador de tu organización.",
    );
  }

  return null;
}

/**
 * Maps any error to a user-friendly message with optional code and details.
 * Delegates to specialized parsers for 409 (conflict) and 422 (validation) errors.
 */
export function mapErrorToUserFriendly(
  error: unknown,
  t: TFunction,
): { message: string; code?: string; details?: Record<string, unknown> } {
  if (!(error instanceof ApiClientError)) {
    return {
      message: error instanceof Error ? error.message : t("validation.general_error"),
    };
  }

  const transportMessage = getNetworkOrServerErrorMessage(error, t);
  if (transportMessage) {
    return {
      message: transportMessage,
      code: error.code,
      details: error.details,
    };
  }

  if (error.status === 403) {
    const forbiddenMessages: Record<string, string> = {
      module_not_enabled: t(
        "error.module.not_enabled",
        "Este modulo no esta habilitado para tu organizacion.",
      ),
      module_expired: t("error.module.expired", "La licencia de este modulo ha expirado."),
      module_quota_exceeded: t(
        "error.module.quota_exceeded",
        "Se ha superado el limite de uso de este modulo.",
      ),
    };
    return {
      message:
        forbiddenMessages[error.code || ""] ||
        t("error.api.forbidden", "No tienes permisos para realizar esta accion."),
      code: error.code,
      details: error.details,
    };
  }

  if (error.status === 409) {
    const info = parseConflictError(error, t);
    return {
      message: info.userMessage,
      code: error.code,
      details: error.details,
    };
  }
  if (error.status === 422) {
    // Check for business logic errors with specific codes first
    const businessMessage = getBusinessLogicMessage(error.code, t);
    if (businessMessage) {
      return {
        message: businessMessage,
        code: error.code,
        details: error.details,
      };
    }
    // Fall through to generic validation error parsing
    const info = parseValidationError(error, t);
    return {
      message: info.userMessage,
      code: error.code,
      details: error.details,
    };
  }

  /**
   * Transport (NETWORK_ERROR/TIMEOUT) and 5xx already handled above by
   * getNetworkOrServerErrorMessage. The remaining codes here cover the few
   * stable, non-status-derived codes the API client may emit for 4xx paths.
   */
  const codeMessages: Record<string, string> = {
    NOT_FOUND: t("error.api.not_found", "Resource not found."),
    FORBIDDEN: t("error.api.forbidden", "You do not have permission to perform this action."),
    BAD_REQUEST: t("error.api.bad_request", "Invalid request. Please check your inputs."),
  };

  return {
    message: codeMessages[error.code || ""] || error.userMessage,
    code: error.code,
    details: error.details,
  };
}

export function getServerErrorReason(error: unknown, t: TFunction): string | null {
  const apiError = findApiClientError(error);
  if (!apiError) return null;
  return mapErrorToUserFriendly(apiError, t).message || null;
}

/**
 * Information about a conflict error with localized message
 */
export interface ConflictErrorInfo {
  /** Whether this is a conflict error (409 status) */
  isConflict: boolean;
  /** The error code from the backend */
  code?: string;
  /** The i18n translation key for this error */
  translationKey?: string;
  /** Parameters to interpolate into the translation */
  translationParams?: Record<string, string>;
  /** The localized user-friendly message */
  userMessage: string;
}

/**
 * Maps backend error codes to i18n translation keys
 */
function getConflictTranslationKey(code: string): string | undefined {
  const codeToKeyMap: Record<string, string> = {
    // Investor errors
    investor_email_duplicate: "error.conflict.investor_email",
    investor_tax_id_duplicate: "error.conflict.investor_tax_id",
    investor_national_id_duplicate: "error.conflict.investor_national_id",
    investor_has_active_investments: "error.conflict.investor_has_active_investments",

    // Beneficiary errors
    beneficiary_email_duplicate: "error.conflict.promoter_email",
    beneficiary_tax_id_duplicate: "error.conflict.promoter_tax_id",
    beneficiary_fiscal_quota_year_duplicate: "error.conflict.promoter_fiscal_quota",
    beneficiary_fiscal_quota_has_investments:
      "error.conflict.promoter_fiscal_quota_has_investments",
    beneficiary_set_primary_legal_rep_failed: "error.conflict.promoter_set_primary_rep",
    beneficiary_set_primary_bank_account_failed: "error.conflict.promoter_set_primary_bank",

    // Fiscal quota errors
    fiscal_quota_frozen: "error.conflict.fiscal_quota_frozen",
    fiscal_quota_has_live_reservations: "error.conflict.fiscal_quota_has_live_reservations",

    // Proposal errors
    investment_proposal_not_awaiting_investment: "error.conflict.proposal_not_awaiting_investment",

    // Classification upload errors
    classification_session_not_open: "error.conflict.classification_upload_session_not_open",

    // Template errors
    template_key_duplicate: "error.conflict.template_key",

    // User errors
    user_email_duplicate: "error.conflict.user_email",

    // Organization errors
    organization_name_duplicate: "error.conflict.organization_name",
    organization_slug_duplicate: "error.conflict.organization_slug",

    // Membership errors
    membership_user_org_duplicate: "error.conflict.membership_exists",
    membership_set_default_failed: "error.conflict.membership_set_default",

    // Invitation errors
    invitation_duplicate_pending: "error.conflict.invitation_duplicate_pending",

    // Project errors
    project_code_duplicate: "error.conflict.project_code",

    // Investment errors
    investment_duplicate: "error.conflict.investment_duplicate",

    // Classification errors
    classification_duplicate_already_resolved:
      "error.conflict.classification_duplicate_already_resolved",
  };

  return codeToKeyMap[code];
}

/**
 * Parses an error and returns conflict error information with localized message
 */
export function parseConflictError(error: unknown, t: TFunction): ConflictErrorInfo {
  // Handle non-ApiClientError errors
  if (!(error instanceof ApiClientError)) {
    return {
      isConflict: false,
      userMessage: error instanceof Error ? error.message : t("validation.general_error"),
    };
  }

  // Handle non-409 errors
  if (error.status !== 409) {
    return {
      isConflict: false,
      userMessage: error.userMessage || t("validation.general_error"),
    };
  }

  // It's a conflict error (409)
  const code = error.code;
  const details = error.details || {};

  // Get the translation key for this error code
  const translationKey = code ? getConflictTranslationKey(code) : undefined;

  // Prepare translation parameters from details
  // Convert all values to strings for interpolation
  const translationParams: Record<string, string> = {};
  Object.entries(details).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      translationParams[key] = String(value);
    }
  });

  // Get the localized message
  let userMessage: string;
  if (translationKey) {
    // Try to get the translation
    const translatedMessage = t(translationKey, translationParams);

    // If translation key was not found, i18next returns the key itself
    // In that case, fall back to generic message
    if (translatedMessage === translationKey) {
      userMessage = t("error.conflict.generic_message");
    } else {
      userMessage = translatedMessage;
    }
  } else {
    // Fallback to generic conflict message
    userMessage = t("error.conflict.generic_message");
  }

  return {
    isConflict: true,
    code,
    translationKey,
    translationParams,
    userMessage,
  };
}

/**
 * Information about validation errors (422 status)
 */
export interface ValidationErrorInfo {
  /** Whether this is a validation error (422 status) */
  isValidationError: boolean;
  /** Array of validation error messages with field names */
  errors: Array<{
    field: string;
    message: string;
    type: string;
  }>;
  /** Localized title for the error */
  title?: string;
  /** Localized summary message */
  userMessage: string;
  /** The raw error message from the backend (useful when errors array is empty) */
  rawMessage?: string;
  /** Error type for special handling (e.g., 'email_domain_invalid') */
  errorType?: string;
  /** Additional context extracted from the error (e.g., { domain: 'gmial.com' }) */
  context?: Record<string, string>;
}

/**
 * Parses a 422 validation error and returns detailed error information
 */
export function parseValidationError(error: unknown, t: TFunction): ValidationErrorInfo {
  // Handle non-ApiClientError errors
  if (!(error instanceof ApiClientError)) {
    return {
      isValidationError: false,
      errors: [],
      userMessage: error instanceof Error ? error.message : t("validation.general_error"),
    };
  }

  // Handle non-422 errors
  if (error.status !== 422) {
    return {
      isValidationError: false,
      errors: [],
      userMessage: error.userMessage || t("validation.general_error"),
    };
  }

  // It's a validation error (422)
  const details = error.details as {
    errors?: Array<{ field: string; message: string; type: string }>;
  };
  const backendErrors = details?.errors || [];

  /**
   * Map backend errors to localized messages.
   * Pydantic v2 emits structured types: "missing", "value_error", "string_too_short",
   * "value_error.email", etc. We use err.type as the source of truth and fall back
   * to a generic field-prefixed message when no i18n key matches.
   */
  const localizedErrors = backendErrors.map((err) => {
    const fieldTranslationKey = `validation.field_names.${err.field}`;
    const fieldName = t(fieldTranslationKey);
    const displayFieldName =
      fieldName !== fieldTranslationKey ? fieldName : err.field.replace("body.", "");

    const errorTypeKey = `validation.field_errors.${err.type}`;
    let errorMessage = t(errorTypeKey, { field: displayFieldName });
    if (errorMessage === errorTypeKey) {
      errorMessage = `${displayFieldName}: ${err.message}`;
    }

    return {
      field: err.field,
      message: errorMessage,
      type: err.type,
    };
  });

  // Get the raw error message from the ApiClientError (without endpoint prefix)
  const rawMessage = error.userMessage;

  let title: string | undefined;
  let userMessage: string;
  let errorType: string | undefined;
  let context: Record<string, string> | undefined;

  /**
   * Email domain validation errors (NXDOMAIN / no MX records).
   * Backend sends EmailValidationErrorCodes.{DOMAIN_NOT_FOUND,NO_MX_RECORDS} as error.code.
   * HulaException.to_dict() does not serialize context, so the affected domain is
   * only available embedded in the message — extract it as a courtesy when possible.
   */
  if (error.code === "email_domain_not_found" || error.code === "email_no_mx_records") {
    const domain = rawMessage?.match(/Domain '([^']+)'/i)?.[1];
    errorType = "email_domain_invalid";
    title = t("error.validation.title");
    if (domain) {
      context = { domain };
      userMessage = t("error.validation.email_domain_invalid", { domain });
    } else {
      userMessage = t("error.validation.email_domain_invalid_generic");
    }
  } else if (localizedErrors[0]) {
    userMessage = localizedErrors[0].message;
  } else if (rawMessage && rawMessage !== "Request validation failed") {
    userMessage = rawMessage;
  } else {
    userMessage = t("validation.request_validation_failed");
  }

  return {
    isValidationError: true,
    errors: localizedErrors,
    title,
    userMessage,
    rawMessage,
    errorType,
    context,
  };
}

/**
 * Maps business logic error codes (422 with specific codes) to i18n translation keys.
 * Returns the translated message if a mapping exists, or undefined to fall through.
 */
function getBusinessLogicMessage(code: string | undefined, t: TFunction): string | undefined {
  if (!code) return undefined;

  const key = `error.business.${code}`;
  const translated = t(key);

  // If i18next returns the key itself, there's no translation — fall through
  if (translated === key) return undefined;
  return translated;
}
